import { useRef, useState } from "react";
import { Tooltip, Typography } from "@altimateai/lego";

interface PlaygroundSessionTitleProps {
  title: string;
}

/**
 * Studio session heading, clamped to two lines. The full title is shown in a
 * tooltip on hover, but only when the title is actually cropped — measured
 * just-in-time when the tooltip is about to open, so it stays correct as the
 * layout (sidebar / window) resizes without watching for resizes.
 */
const PlaygroundSessionTitle = ({ title }: PlaygroundSessionTitleProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    const heading = containerRef.current?.firstElementChild;
    // Suppress the tooltip when the whole title already fits within two lines.
    if (next && heading && heading.scrollHeight <= heading.clientHeight) {
      return;
    }
    setOpen(next);
  };

  return (
    <Tooltip
      title={title}
      open={open}
      onOpenChange={handleOpenChange}
      // Cap the width so a very long title wraps instead of overflowing.
      contentProps={{ className: "al-max-w-2xl al-break-words" }}
    >
      <div ref={containerRef} className="al-mb-2">
        <Typography variant="page-heading" className="al-line-clamp-2">
          {title}
        </Typography>
      </div>
    </Tooltip>
  );
};

export default PlaygroundSessionTitle;
